import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from '../../../context/AuthContext';
import { loginUser } from "../../../services/authService";
import { Mail, User, Lock, Eye, EyeOff } from 'lucide-react';
import confetti from "canvas-confetti";
import { Briefcase } from 'lucide-react'; // if we want to show freelancer icon
import { useDispatch } from "react-redux";
import { patchProfile } from "../../../store/profileSlice"; // adjust path if needed

const LogInForm = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [role, setRole] = useState('user'); // user | freelancer | business
    const [errors, setErrors] = useState({}); // { username, password, general }
    const [shakeUsername, setShakeUsername] = useState(false);
    const [shakePassword, setShakePassword] = useState(false);

    const dispatch = useDispatch();

    const { setAccessToken, setRefreshToken, setRole: setUserRole, setIsRedirecting, setDisplayName, setProfileThumbnail } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});

        // Clear old tokens to prevent stale profile fetch
        localStorage.removeItem("access");
        localStorage.removeItem("refresh");
        localStorage.removeItem("username");
        localStorage.removeItem("first_name");
        localStorage.removeItem("role");
        localStorage.removeItem("avatar");
        setAccessToken(null);
        setUserRole(null);
        setDisplayName(null);
        setProfileThumbnail(null);

        try {
            const response = await loginUser({ email, password, role });
            const { access, username: userName, first_name, role: userRole, expires_at, avatar } = response.data;
            // ✅ Only store access token in localStorage or context
            localStorage.setItem('access', access);
            localStorage.setItem('username', userName);
            localStorage.setItem('first_name', first_name);
            localStorage.setItem('expires_at', expires_at * 1000); // JS expects milliseconds
            localStorage.setItem('role', userRole);
            localStorage.setItem('avatar', avatar);


            // ✅ Update context
            setAccessToken(access);
            setUserRole(userRole);
            setDisplayName(first_name);
            setProfileThumbnail(avatar);


            // Navigate based on role
            switch (userRole) {
                case 'freelancer':
                    navigate('/home/freelancer-dashboard');
                    break;
                case 'business':
                    navigate('/home/business-dashboard');
                    break;
                default:
                    navigate('/home');
            }

        } catch (err) {
            const data = err.response?.data;

            // Clear tokens on failed login
            localStorage.removeItem("access");
            localStorage.removeItem("refresh");
            setAccessToken(null);
            setUserRole(null);
            setDisplayName(null);
            setProfileThumbnail(null);

            if (data?.field === 'email') {
                setErrors({ email: data.error });
            } else if (data?.field === 'password' || data?.error) {
                setErrors({ password: data.error });
            } else {
                setErrors({ general: 'Login failed. Please try again.' });
            }
        }
    };

    return (
        <div className="login-container">
            <form className="login-form" onSubmit={handleSubmit}>
                <h2>
                    {role === 'freelancer'
                        ? 'Freelancer Log In'
                        : role === 'business'
                            ? 'Business Log In'
                            : 'Log In'}
                </h2>

                {/* Role Switch */}
                <div className="login-switch inner-switch">
                    <button
                        type="button"
                        className={`switch-btn ${role === 'user' ? 'active' : ''}`}
                        onClick={() => setRole('user')}
                    >
                        <i className="fa-solid fa-user"></i> User
                    </button>
                    <button
                        type="button"
                        className={`switch-btn ${role === 'freelancer' ? 'active' : ''}`}
                        onClick={() => setRole('freelancer')}
                    >
                        <i className="fa-solid fa-user-tie"></i> Freelancer
                    </button>
                    <button
                        type="button"
                        className={`switch-btn ${role === 'business' ? 'active' : ''}`}
                        onClick={() => setRole('business')}
                    >
                        <i className="fa-solid fa-building"></i> Business
                    </button>
                </div>

                {/* Email field */}
                <div className="form-group with-icon">
                    <Mail className="input-icon" size={18} />
                    <input
                        type="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                    {errors.email && <div className="input-error">{errors.email}</div>}
                </div>

                {/* Password */}
                <div className="password-group">
                    <div className="password-input-wrapper">
                        <Lock className="input-icon" size={18} />
                        <input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                        <span
                            className="toggle-password"
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </span>
                    </div>

                    <div className="login-footer-row">
                        {errors.password && <div className="input-error">{errors.password}</div>}
                        <div
                            className="forgot-password"
                            onClick={() => navigate("/forgot-password", { state: { role } })}
                            style={{ cursor: "pointer" }}
                        >
                            Forgot Password?
                        </div>
                    </div>
                </div>

                {/* Button */}
                <button className="login-button" type="submit">
                    {role === 'freelancer'
                        ? 'Log In as Freelancer'
                        : role === 'business'
                            ? 'Log In as Business'
                            : 'Log In'}
                </button>

                {/* General error at bottom */}
                {errors.general && <div className="form-error">{errors.general}</div>}
            </form>
        </div>
    );
};

export default LogInForm;